/* ==========================================================================
   Airforce Gallery page
   File: assets/build-gallery.js
   - Category filters (with shareable links like /pages/build-gallery#fender-kits)
   - Full-size photo viewer: keyboard arrows, Esc, swipe on phones
   - Works with the Shopify theme editor (live preview reloads, block select)
   No dependencies.
   ========================================================================== */

(() => {
  if (window.customElements.get('build-gallery')) return;

  const SWIPE_DISTANCE = 50;

  class BuildGallery extends HTMLElement {
    connectedCallback() {
      if (!this._initialized) {
        this._initialized = true;
        this.items = Array.from(this.querySelectorAll('[data-afg-item]'));
        this.filterButtons = Array.from(this.querySelectorAll('[data-filter]'));
        this.status = this.querySelector('[data-afg-status]');
        this.emptyState = this.querySelector('[data-afg-empty]');
        this.dialog = this.querySelector('[data-afg-lightbox]');
        this.visibleTriggers = [];
        this.index = 0;

        this.setupFilters();
        this.setupLightbox();
      }

      this._onHashChange = () => {
        const fromHash = this.filterFromHash();
        if (fromHash) this.applyFilter(fromHash, { updateUrl: false });
      };
      window.addEventListener('hashchange', this._onHashChange);
    }

    disconnectedCallback() {
      window.removeEventListener('hashchange', this._onHashChange);
      if (this.dialog && this.dialog.open) this.dialog.close();
      document.documentElement.classList.remove('afg-lock');
    }

    /* ---------- Filters ---------- */

    setupFilters() {
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-filter]');
        if (button && this.contains(button)) {
          this.applyFilter(button.dataset.filter, { updateUrl: true, announce: true });
          return;
        }
        if (event.target.closest('[data-afg-reset]')) {
          this.applyFilter('all', { updateUrl: true, announce: true });
        }
      });

      const initial = this.filterFromHash() || 'all';
      this.applyFilter(initial, { updateUrl: false, announce: false });
    }

    filterFromHash() {
      if (!this.filterButtons.length) return null;
      const hash = decodeURIComponent(window.location.hash.replace('#', '')).toLowerCase();
      if (!hash) return null;
      return this.filterButtons.some((b) => b.dataset.filter === hash) ? hash : null;
    }

    applyFilter(category, { updateUrl = false, announce = false } = {}) {
      this.dataset.activeFilter = category;
      let shown = 0;

      this.items.forEach((item) => {
        const match = category === 'all' || item.dataset.category === category;
        item.hidden = !match;
        if (match) shown += 1;
      });

      let activeButton = null;
      this.filterButtons.forEach((button) => {
        const isActive = button.dataset.filter === category;
        button.setAttribute('aria-pressed', String(isActive));
        if (isActive) activeButton = button;
      });

      if (this.emptyState) this.emptyState.hidden = shown !== 0;

      if (announce && this.status) {
        const label = activeButton
          ? activeButton.querySelector('.afg-filter__label').textContent.trim()
          : '';
        const noun = shown === 1 ? 'photo' : 'photos';
        this.status.textContent =
          category === 'all' ? `Showing all ${shown} ${noun}` : `Showing ${shown} ${noun} in ${label}`;
      }

      if (activeButton && announce) {
        const list = activeButton.parentElement;
        if (list && list.scrollWidth > list.clientWidth) {
          const left = activeButton.offsetLeft - list.clientWidth / 2 + activeButton.offsetWidth / 2;
          list.scrollTo({ left, behavior: 'smooth' });
        }
      }

      if (updateUrl && window.history && window.history.replaceState) {
        const base = window.location.pathname + window.location.search;
        window.history.replaceState(null, '', category === 'all' ? base : `${base}#${category}`);
      }
    }

    /* ---------- Lightbox ---------- */

    setupLightbox() {
      if (!this.dialog) return;
      const d = this.dialog;

      this.lb = {
        img: d.querySelector('[data-afg-lb-img]'),
        stage: d.querySelector('[data-afg-lb-stage]'),
        current: d.querySelector('[data-afg-lb-current]'),
        total: d.querySelector('[data-afg-lb-total]'),
        category: d.querySelector('[data-afg-lb-cat]'),
        caption: d.querySelector('[data-afg-lb-caption]'),
        description: d.querySelector('[data-afg-lb-desc]'),
        link: d.querySelector('[data-afg-lb-link]'),
        prev: d.querySelector('[data-afg-lb-prev]'),
        next: d.querySelector('[data-afg-lb-next]'),
        close: d.querySelector('[data-afg-lb-close]'),
      };

      // Open from a grid tile. Ctrl/Cmd-click still opens the image in a new tab.
      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-afg-open]');
        if (!trigger || !this.contains(trigger) || d.contains(trigger)) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        this.open(trigger);
      });

      this.lb.close.addEventListener('click', () => this.close());
      this.lb.prev.addEventListener('click', () => this.step(-1));
      this.lb.next.addEventListener('click', () => this.step(1));

      // Esc
      d.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });

      d.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        }
      });

      // Click on the dark area around the photo closes the viewer.
      d.addEventListener('click', (event) => {
        if (this._suppressClick) {
          this._suppressClick = false;
          return;
        }
        if (event.target === this.lb.stage || event.target === d) this.close();
      });

      d.addEventListener('close', () => this.onClosed());

      this.lb.img.addEventListener('load', () => this.lb.img.classList.remove('is-loading'));
      this.lb.img.addEventListener('error', () => this.lb.img.classList.remove('is-loading'));

      // Swipe left/right on touch screens.
      this.lb.stage.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch') return;
        this._swipe = { x: event.clientX, y: event.clientY };
      });
      this.lb.stage.addEventListener('pointercancel', () => {
        this._swipe = null;
      });
      this.lb.stage.addEventListener('pointerup', (event) => {
        if (event.pointerType !== 'touch' || !this._swipe) return;
        const dx = event.clientX - this._swipe.x;
        const dy = event.clientY - this._swipe.y;
        this._swipe = null;
        if (Math.abs(dx) > SWIPE_DISTANCE && Math.abs(dx) > Math.abs(dy) * 1.2) {
          this._suppressClick = true;
          window.setTimeout(() => {
            this._suppressClick = false;
          }, 400);
          this.step(dx < 0 ? 1 : -1);
        }
      });
    }

    open(trigger) {
      this.visibleTriggers = this.items
        .filter((item) => !item.hidden)
        .map((item) => item.querySelector('[data-afg-open]'))
        .filter(Boolean);

      this.index = Math.max(0, this.visibleTriggers.indexOf(trigger));
      this.render();

      document.documentElement.classList.add('afg-lock');
      if (!this.dialog.open) this.dialog.showModal();
      this.lb.close.focus();
    }

    close() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    onClosed() {
      document.documentElement.classList.remove('afg-lock');
      // Return to the photo that was last viewed, so browsing continues from there.
      const last = this.visibleTriggers[this.index];
      if (last) {
        last.focus({ preventScroll: true });
        last.scrollIntoView({ block: 'nearest' });
      }
    }

    step(direction) {
      const count = this.visibleTriggers.length;
      if (count < 2) return;
      this.index = (this.index + direction + count) % count;
      this.render();
    }

    render() {
      const trigger = this.visibleTriggers[this.index];
      if (!trigger) return;
      const data = trigger.dataset;
      const { img } = this.lb;
      const count = this.visibleTriggers.length;

      if (img.getAttribute('src') !== data.src) {
        img.classList.add('is-loading');
        img.alt = data.alt || '';
        img.srcset = data.srcset || '';
        img.src = data.src;
        if (img.complete && img.naturalWidth > 1) img.classList.remove('is-loading');
      }

      this.lb.current.textContent = String(this.index + 1);
      this.lb.total.textContent = String(count);
      this.lb.prev.disabled = count < 2;
      this.lb.next.disabled = count < 2;

      this.lb.category.textContent = data.categoryLabel || '';
      this.setText(this.lb.caption, data.caption);
      this.setText(this.lb.description, data.description);

      if (data.link) {
        this.lb.link.href = data.link;
        this.lb.link.textContent = data.linkLabel || 'View product';
        this.lb.link.hidden = false;
      } else {
        this.lb.link.hidden = true;
        this.lb.link.removeAttribute('href');
      }

      this.preload(this.index + 1);
      this.preload(this.index - 1);
    }

    setText(element, value) {
      const text = (value || '').trim();
      element.textContent = text;
      element.hidden = text === '';
    }

    preload(i) {
      const count = this.visibleTriggers.length;
      if (count < 2) return;
      const trigger = this.visibleTriggers[(i + count) % count];
      if (!trigger || trigger._afgPreloaded) return;
      const image = new Image();
      image.sizes = this.lb.img.getAttribute('sizes') || '100vw';
      if (trigger.dataset.srcset) image.srcset = trigger.dataset.srcset;
      image.src = trigger.dataset.src;
      trigger._afgPreloaded = true;
    }

    /* ---------- Theme editor ---------- */

    revealBlock(blockElement) {
      this.close();
      if (blockElement.hidden) this.applyFilter('all');
      blockElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  window.customElements.define('build-gallery', BuildGallery);

  // When you click a "Gallery image" block in the theme editor sidebar,
  // make sure that photo is visible in the preview.
  document.addEventListener('shopify:block:select', (event) => {
    const gallery = event.target.closest && event.target.closest('build-gallery');
    if (gallery && typeof gallery.revealBlock === 'function') gallery.revealBlock(event.target);
  });
})();